/**
 * Window — Phase 2, hosted into walls. Head shape, muntins, size and sill height are
 * per-instance (the head moved off the type 2026-10-30). Vertical extent:
 * level base + sillFt → sillFt + heightFt (+ arch rise).
 *
 * OPERATION (doors/windows round, 2026-08): a TYPE param driving the ELEVATION GLYPH —
 * the drafting-standard dashed sight-lines (apex at the hinge side) and travel arrows —
 * plus the sash geometry (hung/slider two-sash splits). The PLAN symbol stays the
 * standard three glazing lines for every operation — that IS the drafting convention
 * (plan does not encode window operation). See ../openingOps.ts for the taxonomy.
 *
 * OPENING CONFIGURATION GOVERNANCE round (2026-08-10, docs/MODELER.md — Matt's exact
 * motivating example: "casement 30×48 with muntins in a grill pattern, the same size
 * without muntins, the same size as an awning… hundreds of types that are just variants
 * of the same form"): TYPE = the product FORM (~a dozen window types ever — operation +
 * frame). widthFt/heightFt AND muntin pattern/rows/cols moved to INSTANCE — a window
 * with vs. without grilles, or at a different curated size, is the SAME type. Size is
 * constrained to the type's curated `sizeSeries` (real vendor-standard sizes; an
 * off-series pick is allowed but flagged a deviation — see openingSize.ts). protoKeyFor
 * forks the instancing cache on these instance params (the casework-width precedent)
 * since they still shape the prototype mesh.
 */
import type { CategorySchema } from '../../registry'
import { EXTERIOR_TRIM_TYPE_GROUP, EXTERIOR_TRIM_INSTANCE_PARAM, BRICK_LINTEL_INSTANCE_PARAM, INTERIOR_TRIM_INSTANCE_PARAMS } from '../../exteriorTrim'
import { WINDOW_OPERATIONS, WINDOW_OPS_WITH_HAND, WINDOW_OPS_WITH_HEAD_SHAPE, WINDOW_HEAD_TYPES } from '../openingOps'

export const windowSchema: CategorySchema = {
  id: 'window',
  pickRank: 0,
  label: 'Window',
  hostable: { hostsInto: ['wall'], cutsHost: true },
  typeParams: {
    groups: [
      {
        label: 'Dimensions',
        params: [
          // the curated standard-size catalog (SizeSeriesTable in the type editor) —
          // see the door schema's identical param for the full `hidden` rationale.
          { key: 'sizeSeries', label: 'Standard Sizes', kind: 'data', default: [], hidden: true },
          // DOOR REALISM round (2026-08-21): the curated frame make/model catalog —
          // windows had NO frame control at all before this (always the wood
          // constant); see the door schema's identical param + openingFrame.ts.
          { key: 'frameSeries', label: 'Frame Products', kind: 'data', default: [], hidden: true },
        ],
      },
      {
        label: 'Operation',
        params: [
          {
            key: 'operation', label: 'Operation', kind: 'choice', default: 'fixed',
            choices: WINDOW_OPERATIONS.map((o) => ({ value: o.value, label: o.label })),
          },
        ],
      },
      {
        label: 'Appearance',
        params: [
          // P6a: frame/muntin material — glazing keeps the built-in translucent look
          // (a per-type glass material is a recorded non-goal for v1)
          { key: 'surfaceMaterial', label: 'Frame Material', kind: 'material', default: '' },
          // PROJECT STYLE phase 2 (2026-10-06): '' = follow the project's Windows & Doors style
          {
            key: 'frameSystem', label: 'Frame System', kind: 'choice', default: '',
            choices: [
              { value: '', label: '(Project Style)' },
              { value: 'aluminum', label: 'Aluminum' },
              { value: 'clad', label: 'Aluminum-Clad Wood' },
              { value: 'wood', label: 'Wood' },
              { value: 'steel', label: 'Steel' },
              { value: 'fiberglass', label: 'Fiberglass' },
            ],
          },
        ],
      },
      {
        // FLANGE-MOUNT round (2026-09-20, openingFrame.ts#resolveFrameMount) —
        // the door schema's identical param + the same zero-migration
        // reasoning for defaulting to 'centered' rather than 'auto' (see the
        // door schema's Installation group for the full rationale). No
        // Interior Finish counterpart here (casing/kerf were specifically a
        // door ask, Matt 2026-09-20) — a full-depth window frame just draws
        // its usual jambs.
        label: 'Installation',
        params: [
          {
            key: 'frameMountDefault', label: 'Frame Mount', kind: 'choice', default: 'centered',
            choices: [
              { value: 'auto', label: 'Auto (from host wall)' },
              { value: 'exterior', label: 'Exterior Face (Flange-Mounted)' },
              { value: 'centered', label: 'Centered in Wall' },
              { value: 'interior', label: 'Interior Face' },
            ],
          },
        ],
      },
      // EXTERIOR TRIM (2026-10-02) — style + firm-standard sizes; see exteriorTrim.ts
      EXTERIOR_TRIM_TYPE_GROUP,
      {
        // WINDOW SCHEDULE COLUMNS round (2026-08-18, Matt): the firm's printed window
        // schedule carries spec fields no existing param covers — material/finish as
        // SPEC TEXT (distinct from `surfaceMaterial`, the render/viewport appearance
        // hook) plus the glazing makeup, SHGC, and U-Factor. Curated per TYPE, same
        // free-text call as the door schema's identical group. Blank reads as "—" on
        // the printed schedule (openingSchedule.js).
        label: 'Schedule Data',
        params: [
          { key: 'material', label: 'Material', kind: 'text', default: '' },
          { key: 'finish', label: 'Finish', kind: 'text', default: '' },
          { key: 'glazingSpec', label: 'Glazing Spec', kind: 'text', default: '' },
          { key: 'shgc', label: 'SHGC', kind: 'text', default: '' },
          { key: 'uFactor', label: 'U-Factor', kind: 'text', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        // OPENING CONFIGURATION GOVERNANCE: widthFt/heightFt are INSTANCE now, `hidden`
        // — the OpeningSizeField dropdown renders both together (see the door schema's
        // identical group for the full rationale).
        label: 'Size',
        params: [
          { key: 'widthFt', label: 'Width', kind: 'length', default: 4, min: 0.75, max: 20, hidden: true },
          { key: 'heightFt', label: 'Height', kind: 'length', default: 4, min: 0.75, max: 12, hidden: true },
          // DOOR REALISM round: instance frame make/model pick — see the door
          // schema's identical param + openingFrame.ts.
          { key: 'frameProduct', label: 'Frame Product', kind: 'text', default: '', hidden: true },
        ],
      },
      {
        // HEAD SHAPE PER INSTANCE (2026-10-30, Matt: getting to the type to change a head
        // "is friction"): the head is per WINDOW now, not per type — arched/segmental/raked
        // were TYPE params until this round (migrateOpeningHeadsToInstance seeds every
        // existing window from its type at doc open). Only the operations whose sash still
        // works under a shaped head can take one (openingOps.ts#WINDOW_OPS_WITH_HEAD_SHAPE);
        // a slider or awning resolves flat. Order matters: headType resolves before the
        // params conditional on it.
        label: 'Head',
        params: [
          {
            // RAKED WINDOW HEADS round (2026-09-22, Matt): a 'raked' head reads as a
            // straight sloped line, not a curve — its own geometry branch
            // (protos.ts#headZAt), never headRiseFt (that's arch/segmental only; a
            // raked head's rise is the project's rake slope × the window's own
            // width, via the instance rakeSlopeRise override below).
            key: 'headType', label: 'Head Shape', kind: 'choice', default: 'flat',
            choices: WINDOW_HEAD_TYPES.map((o) => ({ value: o.value, label: o.label })),
            conditionalOn: { param: 'operation', equals: [...WINDOW_OPS_WITH_HEAD_SHAPE] },
          },
          {
            key: 'headRiseFt', label: 'Arch Rise', kind: 'length', default: 0.5, min: 0.1, max: 4,
            conditionalOn: { param: 'headType', equals: ['arched', 'segmental'] },
          },
          {
            // RAKED WINDOW HEADS round: which jamb is the HIGH side — a raked head
            // has no symmetric default the way arched/segmental do, so this is a
            // real, visible instance choice (not a flip icon like hand/facing —
            // there's no natural "obviously wrong" starting state to flip away
            // from; the window could rake either direction depending which way the
            // roof it's matching actually slopes).
            key: 'rakeHigh', label: 'Rake Direction', kind: 'choice', default: 'R',
            choices: [{ value: 'L', label: 'High on Left' }, { value: 'R', label: 'High on Right' }],
            conditionalOn: [{ param: 'operation', equals: [...WINDOW_OPS_WITH_HEAD_SHAPE] }, { param: 'headType', equals: 'raked' }],
          },
          {
            // Matt: "the rake slope should be a special parameter accessible by the
            // instance as an override to the default in the model" — null follows
            // the project default (windowRake settings, Templates → Modeler or a
            // future dedicated control; ResolvedElement.windowRake, the
            // coverageDepthFt shadow-default pattern). Same "Slope (rise/12)" unit
            // and label as a roof's own slopeRise, so a value read off the actual
            // roof this window is matching needs no conversion.
            key: 'rakeSlopeRise', label: 'Slope (rise/12)', kind: 'number', default: null, optional: true, min: 0.25, max: 24,
            conditionalOn: [{ param: 'operation', equals: [...WINDOW_OPS_WITH_HEAD_SHAPE] }, { param: 'headType', equals: 'raked' }],
          },
        ],
      },
      {
        label: 'Muntins',
        params: [
          {
            // PROJECT STYLE phase 2 (2026-10-06): '' = the project's Windows & Doors muntins;
            // horizontal / prairie / top-sash-only joined grid (openingStyle.ts#muntinLayout)
            key: 'muntinPattern', label: 'Muntin Pattern', kind: 'choice', default: '',
            choices: [
              { value: '', label: '(Project Style)' },
              { value: 'none', label: 'None' },
              { value: 'grid', label: 'Grid' },
              { value: 'horizontal', label: 'Horizontal Only' },
              { value: 'prairie', label: 'Prairie' },
              { value: 'topSash', label: 'Top Sash Only' },
            ],
          },
          {
            key: 'muntinRows', label: 'Muntin Rows', kind: 'number', default: 2, min: 1, max: 12,
            conditionalOn: { param: 'muntinPattern', equals: ['grid', 'horizontal', 'topSash'] },
          },
          {
            key: 'muntinCols', label: 'Muntin Columns', kind: 'number', default: 2, min: 1, max: 12,
            conditionalOn: { param: 'muntinPattern', equals: ['grid', 'topSash'] },
          },
        ],
      },
      {
        label: 'Placement',
        params: [
          { key: 'hostOffsetFt', label: 'Offset Along Wall', kind: 'length', default: 0, min: 0, positional: true },
          {
            // WINDOW HEAD/SILL round (2026-08-18, Matt: "I want to be able to set
            // windows by head height or sill height"): a window's vertical
            // placement can be dialed in from either end — sillMode chooses which
            // field is the STORED/authoritative one (the topMode/stemMode
            // precedent: one mode + its own field, never both stored at once).
            // The one NOT chosen here is DERIVED at resolve time (doc.ts's
            // resolveElement overrides `sillFt` when sillMode is 'head') — every
            // existing consumer (geometry, plan/section cuts, snap, dimensions,
            // schedules) keeps reading plain `sillFt`/`heightFt` unchanged and
            // automatically sees the correct effective value, no call-site
            // changes needed anywhere else.
            key: 'sillMode', label: 'Set By', kind: 'choice', default: 'sill',
            choices: [
              { value: 'sill', label: 'Sill Height' },
              { value: 'head', label: 'Head Height' },
              // TYPICAL DOOR HEIGHTS (2026-10-08): the head lines up with the project's door height
              // for this window's wall, and moves with it (openingStyle.ts / doc.ts)
              { value: 'doorHead', label: 'Door Head Height (Project)' },
            ],
          },
          {
            key: 'sillFt', label: 'Sill Height', kind: 'length', default: 3, min: 0, max: 12,
            conditionalOn: { param: 'sillMode', equals: 'sill' },
          },
          {
            // the window's HEAD above finish floor — sill derives as headHeightFt
            // − heightFt (clamped to 0) at resolve time, so raising/lowering a
            // window's height while in this mode keeps its HEAD line fixed
            // (the whole point of dialing in by head height — aligning a run of
            // windows at one consistent head, regardless of each one's own size).
            key: 'headHeightFt', label: 'Head Height', kind: 'length', default: 6 + 8 / 12, min: 0.75, max: 16,
            conditionalOn: { param: 'sillMode', equals: 'head' },
          },
          {
            // hinge side (casement/tilt-turn) or operable-sash side (slider) — per
            // instance, active only when the type's operation has a handing at all
            // (cross-schema conditionalOn, resolved with the type params as context).
            // FLIP HANDLES round (Matt, 2026-09-21): hidden from the form panel —
            // edited via the flip icon on the plan symbol (or Spacebar), same as a
            // door's hand/facing.
            key: 'hand', label: 'Hinge Side', kind: 'choice', default: 'L', hidden: true,
            choices: [{ value: 'L', label: 'Left' }, { value: 'R', label: 'Right' }],
            conditionalOn: { param: 'operation', equals: [...WINDOW_OPS_WITH_HAND] },
          },
          // WINDOW SCHEDULE COLUMNS round: `tagOverride` MOVED here from a type param
          // (Identification group, above) — windows now mint a per-INSTANCE mark, same
          // as doors, so a shared type-level override would incorrectly pin every
          // instance of that type to identical text, undermining the whole point of
          // per-instance identity. Empty = the derived per-instance mark (kernel
          // tags.ts / openingConfig.ts#resolveOpeningInstanceMarks).
          { key: 'tagOverride', label: 'Tag Override', kind: 'text', default: '' },
          {
            // '' inherits the type default — see the door schema's identical
            // param for the full rationale.
            key: 'frameMount', label: 'Frame Mount Override', kind: 'choice', default: '',
            choices: [
              { value: '', label: '(Use Type Default)' },
              { value: 'exterior', label: 'Exterior Face (Flange-Mounted)' },
              { value: 'centered', label: 'Centered in Wall' },
              { value: 'interior', label: 'Interior Face' },
            ],
          },
          // PROJECT STYLE phase 2: an exterior-mounted unit's recess behind the exterior finish
          // face (blank = the project's, else flush to the sheathing)
          { key: 'frameRecessFt', label: 'Recess From Exterior Face', kind: 'length', default: null, optional: true, optionalHint: 'project standard', min: 0, max: 2 },
          { key: 'frameSystem', label: 'Frame System Override', kind: 'choice', default: '', choices: [
              { value: '', label: '(Type / Project Style)' },
              { value: 'aluminum', label: 'Aluminum' },
              { value: 'clad', label: 'Aluminum-Clad Wood' },
              { value: 'wood', label: 'Wood' },
              { value: 'steel', label: 'Steel' },
              { value: 'fiberglass', label: 'Fiberglass' },
            ] },
          // the frame's colors (colors.ts refs — a color or a palette role); blank = the palette
          { key: 'frameColorExterior', label: 'Frame Color (Exterior)', kind: 'data', default: null, hidden: true },
          { key: 'frameColorInterior', label: 'Frame Color (Interior)', kind: 'data', default: null, hidden: true },
          // PAINT BRUSH (2026-10-07): the brushed color (a colors.ts ref) — blank = its finish / role
          { key: 'trimColor', label: 'Trim Color (Exterior)', kind: 'data', default: null, hidden: true },
          { key: 'interiorTrimColor', label: 'Trim Color (Interior)', kind: 'data', default: null, hidden: true },
          // EXTERIOR TRIM (2026-10-02): this opening's own trim — '' = the type's
          EXTERIOR_TRIM_INSTANCE_PARAM,
          BRICK_LINTEL_INSTANCE_PARAM,
          // INTERIOR TRIM (2026-10-04): casings on the room side, per window
          ...INTERIOR_TRIM_INSTANCE_PARAMS,
        ],
      },
      {
        // SHUTTERS (2026-10-04, Matt: "turn on/off shutters on windows as an instance
        // parameter … a few styles … one sided or two sides and flip the side … cover the
        // window when closed … hinges and stays … a louvered bahama style shutter …
        // arm stays that hold it out partially open"). Built by the HOST WALL next to the
        // exterior trim (geometry wall/shutters.ts) — it knows the wall face and casing.
        label: 'Shutters',
        params: [
          {
            key: 'shutterStyle', label: 'Shutters', kind: 'choice', default: '',
            choices: [
              { value: '', label: 'None' },
              { value: 'louvered', label: 'Louvered' },
              { value: 'raisedPanel', label: 'Raised Panel' },
              { value: 'boardBatten', label: 'Board & Batten' },
              { value: 'bahama', label: 'Bahama (top-hinged, louvered)' },
            ],
          },
          {
            key: 'shutterSides', label: 'Sides', kind: 'choice', default: 'both',
            choices: [{ value: 'both', label: 'Both Sides (pair)' }, { value: 'one', label: 'One Side' }],
            conditionalOn: { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'boardBatten'] },
          },
          {
            key: 'shutterSide', label: 'Side', kind: 'choice', default: 'L',
            choices: [{ value: 'L', label: 'Left (from outside)' }, { value: 'R', label: 'Right (from outside)' }],
            conditionalOn: [
              { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'boardBatten'] },
              { param: 'shutterSides', equals: 'one' },
            ],
          },
          {
            key: 'shutterState', label: 'Position', kind: 'choice', default: 'open',
            choices: [{ value: 'open', label: 'Open' }, { value: 'closed', label: 'Closed (covers the window)' }],
            conditionalOn: { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'boardBatten', 'bahama'] },
          },
          {
            // MIDDLE RAILS (2026-10-04, Matt): how many rails split the leaf into equal
            // fields — louvered, raised panel and Bahama (board & batten has none)
            key: 'shutterRails', label: 'Middle Rails', kind: 'number', default: 1, min: 0, max: 6,
            conditionalOn: { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'bahama'] },
          },
          {
            // how far the arm stays prop a Bahama shutter out from the wall
            key: 'bahamaAngleDeg', label: 'Propped Open (°)', kind: 'number', default: 35, min: 5, max: 75,
            conditionalOn: [
              { param: 'shutterStyle', equals: 'bahama' },
              { param: 'shutterState', equals: 'open' },
            ],
          },
          {
            // '' = the firm's Painted Shutter
            key: 'shutterMaterial', label: 'Shutter Material', kind: 'material', default: '',
            conditionalOn: { param: 'shutterStyle', equals: ['louvered', 'raisedPanel', 'boardBatten', 'bahama'] },
          },
          // COLORS (2026-10-06): the Paint Brush's color on the shutters (colors.ts id), 3D only
          { key: 'shutterColor', label: 'Shutter Color', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        // per-instance: two windows of the identical type/size can differ on egress
        // compliance depending on sill height / clear opening at that placement (the
        // door schema's identical reasoning for Hardware Group/Egress).
        label: 'Schedule Data',
        params: [
          { key: 'egress', label: 'Egress Window', kind: 'bool', default: false },
        ],
      },
    ],
  },
}
